import { useEffect, useRef, useState, type ReactNode } from "react";
import { Check, ChevronDown, CircleDot, Cloud, CloudAlert, CloudOff, CloudUpload, Loader2, Snowflake } from "@fg/icons";
import { cn } from "@/lib/utils";
import type { SaveStatus } from "@/lib/useSaveTracker";
import type { SelectionSummary, WorkbookEditorHandle } from "@/features/workbook/WorkbookEditor";
import { cellName, colName } from "@/features/workbook/geometry";

// Header "Freeze" quick menu and the Excel-style selection summary
// (Average / Count / Sum …) under the grid. Both are fed by the editor: the
// summary through its onSelectionSummary callback, the freeze state through
// its handle.

// ── Selection summary ───────────────────────────────────────────────────────

export type SelectionAggregate = SelectionSummary;

/** Selections larger than this aren't summarised (the editor reports them as truncated). */
const MAX_CELLS = 20_000;

const numberFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 6 });

/**
 * Mock `.status`: a slim 28px bar under the sheet tabs — "Ready" and the
 * selection's address on the left; Average / Count / Min / Max / Sum of the
 * selection (two or more cells) and the save state on the right. Read-only:
 * the summary comes from the sheet model and never touches a cell.
 */
export function SheetStatusBar({ aggregate, status, inDrive, loading, onRetry }: {
    aggregate: SelectionAggregate | null;
    status: SaveStatus;
    inDrive: boolean;
    loading: boolean;
    onRetry: () => void;
}) {
    const a = aggregate;
    const show = a !== null && a.count >= 2;
    const numeric = show && a.numericCount > 0;
    return (
        <div
            role="group"
            aria-label="Status bar"
            className="flex h-7 shrink-0 items-center gap-4 overflow-hidden border-t bg-[var(--sheets-pane)] px-3 text-xs text-muted-foreground"
        >
            <span className="shrink-0">{loading ? "Opening…" : "Ready"}</span>
            {a ? <span className="hidden shrink-0 tabular-nums sm:inline" title="Selection">{a.address}</span> : null}
            <div aria-label="Selection summary" className="ml-auto flex min-w-0 items-center gap-4 whitespace-nowrap">
                {show ? (
                    <>
                        {numeric ? <Stat label="Average" value={numberFormat.format(a.sum / a.numericCount)} /> : null}
                        <Stat label="Count" value={numberFormat.format(a.count)} />
                        {numeric && a.numericCount !== a.count ? (
                            <Stat label="Numerical count" value={numberFormat.format(a.numericCount)} className="hidden xl:inline" />
                        ) : null}
                        {numeric ? <Stat label="Min" value={numberFormat.format(a.min)} className="hidden md:inline" /> : null}
                        {numeric ? <Stat label="Max" value={numberFormat.format(a.max)} className="hidden md:inline" /> : null}
                        {numeric ? <Stat label="Sum" value={numberFormat.format(a.sum)} /> : null}
                        {a.truncated ? (
                            <span title={`Very large selection — only the first ${MAX_CELLS.toLocaleString()} cells were counted`}>≈</span>
                        ) : null}
                    </>
                ) : null}
            </div>
            <span aria-hidden className="h-3.5 w-px shrink-0 bg-border" />
            <SheetSaveStatusItem status={status} inDrive={inDrive} loading={loading} onRetry={onRetry} />
        </div>
    );
}

function Stat({ label, value, className }: { label: string; value: string; className?: string }) {
    return (
        <span className={cn("tabular-nums", className)}>
            {label} <span className="font-semibold text-foreground">{value}</span>
        </span>
    );
}

/** Cloud with a tick — the icon set has no CloudCheck. */
function CloudSaved() {
    return (
        <span aria-hidden className="relative inline-flex h-3.5 w-3.5 shrink-0">
            <Cloud className="h-3.5 w-3.5" />
            <Check className="absolute left-1/2 top-[58%] h-2 w-2 -translate-x-1/2 -translate-y-1/2" strokeWidth={3} />
        </span>
    );
}

/**
 * The save state for the status bar (as Docs' DocSaveStatusItem) — the same
 * tracker the title uses; a failed save retries on click.
 */
function SheetSaveStatusItem({ status, inDrive, loading, onRetry }: {
    status: SaveStatus;
    inDrive: boolean;
    loading: boolean;
    onRetry: () => void;
}) {
    const base = "inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded px-1.5 text-xs";
    const muted = cn(base, "text-muted-foreground");
    const label = (text: string) => <span className="max-sm:sr-only">{text}</span>;
    let content: ReactNode;
    if (loading) {
        content = <span className={muted}><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />{label("Opening…")}</span>;
    } else if (status === "saving") {
        content = <span className={muted}><CloudUpload className="h-3.5 w-3.5" aria-hidden />{label("Saving…")}</span>;
    } else if (status === "failed") {
        content = (
            <button
                type="button"
                onClick={onRetry}
                title="Try saving again"
                className={cn(base, "font-medium text-destructive hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary")}
            >
                <CloudAlert className="h-3.5 w-3.5" aria-hidden />{label("Save failed — retry")}
            </button>
        );
    } else if (status === "dirty") {
        content = <span className={muted}><CircleDot className="h-3.5 w-3.5" aria-hidden />{label("Unsaved changes")}</span>;
    } else if (inDrive) {
        content = <span className={muted}><CloudSaved />{label("Saved to Drive")}</span>;
    } else {
        content = (
            <span className={muted} title="Save (Ctrl+S) to keep this workbook in Drive">
                <CloudOff className="h-3.5 w-3.5" aria-hidden />{label("Not in Drive")}
            </span>
        );
    }
    return <div role="status" aria-live="polite" className="flex shrink-0 items-center">{content}</div>;
}

// ── Freeze quick menu ───────────────────────────────────────────────────────

interface FreezeState { rows: number; cols: number; active: string; row: number; col: number }

export function FreezeMenu({ editor, onError }: {
    editor: { current: WorkbookEditorHandle | null };
    onError: (message: string) => void;
}) {
    const [open, setOpen] = useState(false);
    const [state, setState] = useState<FreezeState>({ rows: 0, cols: 0, active: "A1", row: 0, col: 0 });
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const readState = (): FreezeState | null => {
        const f = editor.current?.getFreeze();
        if (!f) return null;
        return { rows: f.rows, cols: f.cols, active: cellName(f.row, f.col), row: f.row, col: f.col };
    };

    useEffect(() => {
        if (!open) return;
        menuRef.current?.querySelector<HTMLButtonElement>('[role^="menuitem"]:not([disabled])')?.focus();
        const onPointer = (e: PointerEvent) => {
            const t = e.target as Node;
            if (!menuRef.current?.contains(t) && !buttonRef.current?.contains(t)) setOpen(false);
        };
        document.addEventListener("pointerdown", onPointer, true);
        return () => document.removeEventListener("pointerdown", onPointer, true);
    }, [open]);

    const close = (refocus: boolean) => {
        setOpen(false);
        if (refocus) buttonRef.current?.focus();
    };
    const toggle = () => {
        if (!open) {
            const s = readState();
            if (s) setState(s);
        }
        setOpen((v) => !v);
    };
    const apply = (rows: number, cols: number) => {
        close(true);
        const ed = editor.current;
        if (!ed) return;
        ed.setFreeze(rows, cols).catch((err: unknown) => onError(err instanceof Error ? err.message : String(err)));
    };
    const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]:not([disabled])') ?? []);
        const at = items.indexOf(document.activeElement as HTMLButtonElement);
        let target: HTMLButtonElement | undefined;
        if (e.key === "ArrowDown") target = items[(at + 1) % items.length];
        else if (e.key === "ArrowUp") target = items[(at - 1 + items.length) % items.length];
        else if (e.key === "Home") target = items[0];
        else if (e.key === "End") target = items[items.length - 1];
        else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(true); return; }
        else if (e.key === "Tab") { setOpen(false); return; }
        if (target) { e.preventDefault(); target.focus(); }
    };

    const { rows, cols, active, row, col } = state;
    const upToHint = row > 0 && col > 0
        ? `Rows 1–${row} and columns A–${colName(col - 1)} stay in view`
        : row > 0
            ? `Rows 1–${row} stay in view`
            : col > 0
                ? `Columns A–${colName(col - 1)} stay in view`
                : "Select a cell below or right of A1 first";
    const summary = rows === 0 && cols === 0
        ? "Nothing is frozen"
        : `Frozen: ${rows} row${rows === 1 ? "" : "s"}, ${cols} column${cols === 1 ? "" : "s"}`;
    const itemClass = "flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent focus-visible:bg-accent focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50";

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label="Freeze rows and columns"
                title="Freeze rows and columns"
                onClick={toggle}
                onKeyDown={(e) => { if (e.key === "ArrowDown" && !open) { e.preventDefault(); toggle(); } }}
                className="suite-appbar-pill"
            >
                <Snowflake className="h-3.5 w-3.5" aria-hidden />
                <span className="hidden md:inline">Freeze</span>
                <ChevronDown className="h-3.5 w-3.5 opacity-80" aria-hidden />
            </button>
            {open ? (
                <div
                    ref={menuRef}
                    role="menu"
                    aria-label="Freeze"
                    onKeyDown={onMenuKeyDown}
                    className="absolute right-0 top-full z-[70] mt-1 w-72 rounded-md border bg-popover p-1 text-popover-foreground shadow-lg"
                >
                    <button type="button" role="menuitemcheckbox" aria-checked={rows === 1} onClick={() => apply(rows === 1 ? 0 : 1, cols)} className={itemClass}>
                        <span className="mt-0.5 w-4 shrink-0">{rows === 1 ? <Check className="h-4 w-4 text-primary" aria-hidden /> : null}</span>
                        <span>First row</span>
                    </button>
                    <button type="button" role="menuitemcheckbox" aria-checked={cols === 1} onClick={() => apply(rows, cols === 1 ? 0 : 1)} className={itemClass}>
                        <span className="mt-0.5 w-4 shrink-0">{cols === 1 ? <Check className="h-4 w-4 text-primary" aria-hidden /> : null}</span>
                        <span>First column</span>
                    </button>
                    <button type="button" role="menuitem" disabled={row === 0 && col === 0} onClick={() => apply(row, col)} className={itemClass}>
                        <span className="mt-0.5 w-4 shrink-0" />
                        <span className="min-w-0">
                            <span className="block">Up to current cell ({active})</span>
                            <span className="block text-xs text-muted-foreground">{upToHint}</span>
                        </span>
                    </button>
                    <div role="separator" className="my-1 h-px bg-border" />
                    <button type="button" role="menuitem" disabled={rows === 0 && cols === 0} onClick={() => apply(0, 0)} className={itemClass}>
                        <span className="mt-0.5 w-4 shrink-0" />
                        <span>Unfreeze</span>
                    </button>
                    <p className={cn("px-2 pb-1 pt-1.5 text-xs text-muted-foreground")}>{summary}</p>
                </div>
            ) : null}
        </div>
    );
}
